import React, { useRef, PropsWithChildren, Fragment, CSSProperties } from 'react';
import { ThreeDots } from 'react-loader-spinner';
import { CheckIcon } from 'icons/';
import { Helpers } from '../Helpers';
import './checkbox.scss';
import config from '../config';

type CheckBoxProps = {
    isSelected: boolean;
    toggleMode?: boolean,    
    isDisabled?: boolean,    
    isTitleStyle?: boolean,
    headerLabel?: string,
    checkboxStyle?: 'solid' | undefined,
    checkboxLabel?: string,
    checkboxTooltip?: string,
    width?: string,
    height?: string,
    isProcessing?: boolean,
    flexGrow?: boolean,
    optionStyle?: boolean,
    featureStyle?: boolean,
    noHover?: boolean,
    datafor?: string,
    showTooltip?: boolean;
    nbrItems?: string,
    labelOnLeft?: boolean,
    tabIndex?: number,  
    targetClassName?: string,
    margin?: string,
    padding?: string,
    onCheckboxToggled(isSelected: boolean): void,
    onMouseOver?(refElement: React.RefObject<HTMLDivElement>): void,
    onMouseOut?(): void, 
    onChildrenKeyDown?(event: React.KeyboardEvent<HTMLInputElement>): void   
}

export function CheckBox(props: PropsWithChildren<CheckBoxProps>) {
    const { showTooltip } = props,        
        elementRef = useRef<HTMLDivElement | null>(null),
        //borderClassName: string = props.headerLabel ? '' : '',
        activeClassName: string = props.isSelected ? 'active' : '',
        disabledClassName: string = props.isDisabled ? 'is-disabled' : '',
        titleStyleClassName: string = props.isTitleStyle ? 'title' : '',
        flexGrowClassName: string = props.flexGrow ? 'full-width' : '',
        noHoverClassName: string = props.noHover ? 'no-hover' : '',
        fontClassName: string = props.optionStyle ? 'option-style' : props.featureStyle ? 'feature-style' : '',
        checkboxStyleClassName: string = props.checkboxStyle ? `${props.checkboxStyle}` : '',
        targetClassName: string = props.targetClassName || '',
        widthStyle: CSSProperties = props.width ? { width: props.width } : {},
        heightStyle: CSSProperties = props.height ? { height: props.height } : {},
        paddingStyle: CSSProperties = props.padding ? { padding: props.padding } : {},
        marginStyle: CSSProperties = props.margin ? { margin: props.margin } : {},
        isProcessing: boolean = props.isProcessing === true,
        numberOfItems: string = props.nbrItems ? props.nbrItems : '',
        loaderColor: string = config.loaderColor,
        loading: JSX.Element = (
            <div className="checkbox__loader"><ThreeDots color={loaderColor} height={20} width={20} /></div>
        ),
        
        handleToggleClick = (event: React.MouseEvent<HTMLDivElement>) => {
            if (!props.isDisabled) {
                event.stopPropagation();
                event.preventDefault();
                props.onCheckboxToggled(!props.isSelected);
            }
        },        
        handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'Enter' && props.onChildrenKeyDown) {
                props.onChildrenKeyDown(event);
            } else if (event.key === 'Enter') {
                props.onCheckboxToggled(!props.isSelected);
            }
        },
        handleMouseOver = () => {
            if (props.onMouseOver) {
                props.onMouseOver(elementRef);
            }
        },
        handleMouseOut = () => {
            if (props.onMouseOut) {
                props.onMouseOut();
            }
        };

    return (
        <Fragment>
            <div className={`checkbox__container ${noHoverClassName} ${disabledClassName} ${flexGrowClassName} ${titleStyleClassName} ${targetClassName} ${checkboxStyleClassName}`}
                ref={elementRef}
                style={{ ...heightStyle, ...widthStyle, ...paddingStyle, ...marginStyle }}
                onClick={props.headerLabel ? handleToggleClick : undefined}
                onKeyDown={props.headerLabel ? handleKeyDown : undefined}
                onMouseOver={handleMouseOver}
                onMouseOut={handleMouseOut}
                onFocus={handleMouseOver}
                onBlur={handleMouseOut}
                data-tooltip-id={props.datafor || undefined}
                data-tooltip-content={!showTooltip ? '' : props.checkboxTooltip ? props.checkboxTooltip : props.checkboxLabel}
                data-tooltip-delay-show={1500}                
                data-tooltip-place='bottom'                                
            >
                {props.headerLabel &&
                    <div className={`checkbox__header `}>{props.headerLabel}</div>
                }
                {isProcessing && loading}

                <div className={`checkbox__content`}
                    tabIndex={props.tabIndex} 
                    onKeyDown={handleKeyDown}
                >
                    {props.labelOnLeft === true && props.children && 
                        props.children
                    }
                    {props.labelOnLeft === true && !props.children &&                        
                        <div className={`checkbox__label left ${disabledClassName}`}
                            onClick={handleToggleClick}
                            onKeyDown={handleKeyDown}
                            tabIndex={-1} 
                        >
                            <p className={`${fontClassName}`}>
                                {props.checkboxLabel}
                                {numberOfItems !== '' &&
                                    <span className='entry-label__nbr-items'>                                            
                                        ({numberOfItems})                                            
                                    </span>
                                } 
                            </p>
                        </div>
                    }

                    {!props.toggleMode &&
                        <div className={`checkbox__checkmark ${checkboxStyleClassName} ${activeClassName} ${disabledClassName}`}
                            onClick={handleToggleClick}
                            onKeyDown={handleKeyDown}
                            tabIndex={-1} 
                        >
                            {activeClassName === 'active' && 
                                <div className='checkbox__checkmark-active-inner'>
                                    <CheckIcon width={12} height={12} />
                                </div>
                            }
                        </div>
                    }
                    {props.toggleMode &&
                        <div className={`checkbox-toggle-switch ${activeClassName} ${disabledClassName}`} onClick={handleToggleClick} tabIndex={-1} >
                            <span className={`checkbox-toggle-slider round ${activeClassName} ${disabledClassName}`} onChange={handleToggleClick} ></span>
                        </div>
                    }
                    {props.labelOnLeft !== true && props.children && 
                        props.children
                    }
                    {props.labelOnLeft !== true && !props.children && 
                        <div className={`checkbox__label ${disabledClassName}`}
                            onClick={handleToggleClick}
                            onKeyDown={handleKeyDown}
                            tabIndex={-1} 
                        >
                            <p className={`${fontClassName}`}>
                                {props.checkboxLabel}
                                {numberOfItems !== '' &&
                                    <span className='entry-label__nbr-items'>                                            
                                        ({numberOfItems})                                            
                                    </span>
                                } 
                            </p>
                        </div>
                    }
                </div>
            </div>
        </Fragment>
    );
}